import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Lock, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useBag } from "../../context/BagContext";
import { IMAGES } from "../../data/product";
import { trackAddToCart, trackInitiateCheckout } from "../../lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BagItem = ({ quantity, setQuantity, price, money, product }) => (
  <div className="flex gap-5" data-testid="bag-item">
    <img src={IMAGES.heroBottle} alt="CELUSTA Leave-In Conditioner" className="w-20 h-28 object-cover rounded-lg border border-[rgba(58,42,37,0.1)]" />
    <div className="flex-1">
      <p className="font-serif-display text-lg text-[#2B1F1B]">CELUSTA Leave-In Conditioner</p>
      <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#9A8D85] mt-1">200 ml — Leave-in</p>
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center border border-[rgba(58,42,37,0.2)] rounded-full px-1">
          <button onClick={() => setQuantity(Math.max(0, quantity - 1))} className="p-2 text-[#756A64] hover:text-[#3A2A25] transition-colors duration-300" data-testid="bag-qty-decrease">
            <Minus size={14} />
          </button>
          <span className="w-8 text-center text-sm font-medium" data-testid="bag-qty-value">{quantity}</span>
          <button onClick={() => { setQuantity(quantity + 1); trackAddToCart(product, 1); }} className="p-2 text-[#756A64] hover:text-[#3A2A25] transition-colors duration-300" data-testid="bag-qty-increase">
            <Plus size={14} />
          </button>
        </div>
        <span className="text-sm font-medium text-[#2B1F1B]" data-testid="bag-item-total">
          {money(price * quantity)}
        </span>
      </div>
    </div>
  </div>
);

export const BagDrawer = ({ product }) => {
  const { quantity, setQuantity, open, setOpen } = useBag();
  const [loading, setLoading] = useState(false);
  const price = product?.price ?? 29.99;
  const currency = product?.currency ?? "USD";
  const money = (amount) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, setOpen]);

  const stripeFallbackCheckout = async () => {
    const res = await axios.post(`${API}/payments/checkout`, {
      lookup_key: "celusta_purple_spray",
      quantity,
      origin_url: window.location.origin,
    });
    window.location.href = res.data.checkout_url;
  };

  const checkout = async () => {
    if (quantity === 0) return;
    setLoading(true);
    try {
      const cartId = localStorage.getItem("shopify_cart_id");
      const res = await axios.post(`${API}/shopify/checkout`, {
        quantity,
        cart_id: cartId || null,
      });
      if (res.data.cart_id) {
        localStorage.setItem("shopify_cart_id", res.data.cart_id);
      }
      trackInitiateCheckout(product, quantity);
      window.location.href = res.data.checkout_url;
    } catch (err) {
      // Automatic fallback to the existing Stripe checkout
      try {
        await stripeFallbackCheckout();
      } catch (fallbackErr) {
        toast.error(fallbackErr.response?.data?.detail || "Could not start checkout. Please try again.");
        setLoading(false);
      }
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] bg-[#241813]/40 backdrop-blur-sm"
            data-testid="bag-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md bg-[#F8F4EC] border-l border-[rgba(58,42,37,0.1)] flex flex-col"
            data-testid="bag-drawer"
          >
            <div className="flex items-center justify-between px-8 py-6 border-b border-[rgba(58,42,37,0.1)]">
              <h3 className="font-serif-display text-2xl text-[#2B1F1B]">Your Bag</h3>
              <button onClick={() => setOpen(false)} className="p-2 text-[#756A64] hover:text-[#3A2A25] transition-colors duration-300" data-testid="bag-close-button">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-8">
              {quantity === 0 ? (
                <p className="text-sm font-light text-[#9A8D85]" data-testid="bag-empty-message">Your bag is empty.</p>
              ) : (
                <BagItem quantity={quantity} setQuantity={setQuantity} price={price} money={money} product={product} />
              )}
            </div>

            <div className="px-8 py-8 border-t border-[rgba(58,42,37,0.1)] bg-[#FFFDF9] space-y-5">
              <div className="flex justify-between text-sm">
                <span className="text-[#9A8D85]">Subtotal</span>
                <span className="font-medium text-[#2B1F1B]" data-testid="bag-subtotal">{money(price * quantity)}</span>
              </div>
              <button
                onClick={checkout}
                disabled={quantity === 0 || loading}
                className="w-full px-8 py-4 bg-[#3A2A25] text-white text-xs font-semibold uppercase tracking-[0.2em] rounded-full hover:bg-[#55423A] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="checkout-button"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                {loading ? "Redirecting…" : "Checkout"}
              </button>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#9A8D85] text-center" data-testid="checkout-secure-note">
                Secure checkout — test mode
              </p>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
